/**
 * ExUsProfessionalsV3
 * Flag: exUsProfessionalsV3
 * API: POST api/v3/exusprofessionals/search  (paginated, IsExUsUser policy)
 *
 * Full rewrite of ExUsSearch.tsx (v2):
 *   - react-hook-form for search/filter state (no class component)
 *   - RTK Query useMutation (no raw apiFetch)
 *   - AG Grid Community for results (no custom table)
 *   - Server-side pagination via pageNumber/pageSize
 *   - Ant Design Drawer for preview
 *   - AddExUs wizard preserved from v2
 */
import React, { useCallback, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { Drawer } from 'antd';

import styles from './ExUsProfessionalsV3.module.css';
import { useSearchExUsV3Mutation } from '../../api/exUsProfessionalsV3Api';
import type { ExUsSearchRowV3 } from '../../api/exUsProfessionalsV3Api';
import SearchLanding from '../../globalcomponents/SearchLanding';
import { useExusDetailsMutation } from '../../api/exusApi';

import SearchPanel, { type SearchFormValues } from './components/SearchPanel';
import FilterPanel from './components/FilterPanel';
import ExUsGrid from './components/ExUsGrid';
import PaginationBar from './components/PaginationBar';
import AddExUs from '../ExUsSearch/components/AddExUs';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface Props {
    // user prop mirrors v2 pattern — passed down from App.tsx / AuthorizeRoute
    user?: {
        team?:      string;
        terr?:      string;
        email?:     string;
        userName?:  string;
        role?:      string;
    };
}

// Default form values
const DEFAULT_VALUES: SearchFormValues = {
    firstName:   '',
    lastName:    '',
    city:        '',
    countryCode: '',
    state:       '',
    postalCode:  '',
    profId:      '',
    degree:      '',
    specialty:   '',
    universe:    '',
};

// ---------------------------------------------------------------------------
// Preview panel content
// ---------------------------------------------------------------------------

function PreviewContent({ row }: { row: ExUsSearchRowV3 }) {
    const fullName = [row.firstName, row.lastName].filter(Boolean).join(' ');
    const address  = [row.address1, row.address2, row.city, row.state, row.postalCode, row.countryCode]
        .filter(Boolean).join(', ');

    return (
        <div style={{ fontSize: 14, lineHeight: 1.7 }}>
            <div style={{ fontWeight: 700, fontSize: 16, marginBottom: 8 }}>
                {fullName}{row.suffix ? `, ${row.suffix}` : ''}{row.degree ? `, ${row.degree}` : ''}
            </div>
            {row.specialty && <div><strong>Specialty:</strong> {row.specialty}</div>}
            {address       && <div><strong>Address:</strong> {address}</div>}
            {row.phone     && <div><strong>Phone:</strong> {row.phone}</div>}
            {row.email     && <div><strong>Email:</strong> {row.email}</div>}
            {row.profId    && <div><strong>Prof ID:</strong> {String(row.profId)}</div>}
            {row.terrId    && <div><strong>Territory:</strong> {row.terrId}</div>}
            <div style={{ marginTop: 12 }}>
                <strong>CTMS:</strong> {row.ctmsFlag === 'Y' ? '✔' : '—'}
                {'  '}
                <strong>Spend:</strong> {row.aggspendFlag === 'Y' ? '✔' : '—'}
            </div>
        </div>
    );
}

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

export default function ExUsProfessionalsV3({ user }: Props) {
    const { register, handleSubmit, watch, setValue, reset, getValues } =
        useForm<SearchFormValues>({ defaultValues: DEFAULT_VALUES });

    // Pagination state
    const [pageNumber, setPageNumber]   = useState(1);
    const [pageSize,   setPageSize]     = useState(20);

    // We store the last-committed search params so page changes can re-run
    // with the same filters without requiring the user to re-submit the form.
    const lastSearchRef = useRef<SearchFormValues>(DEFAULT_VALUES);

    // RTK Query
    const [searchExUsV3, { data, isLoading, isError }] = useSearchExUsV3Mutation();
    const [fetchDetails] = useExusDetailsMutation();

    // Preview drawer
    const [previewRow,       setPreviewRow]       = useState<ExUsSearchRowV3 | null>(null);
    const [previewOpen,      setPreviewOpen]       = useState(false);

    // Add Ex-US wizard
    const [showAddWizard,    setShowAddWizard]     = useState(false);

    // -----------------------------------------------------------------------
    // Helpers
    // -----------------------------------------------------------------------

    const runSearch = useCallback(
        (values: SearchFormValues, page: number, size: number) => {
            lastSearchRef.current = values;
            searchExUsV3({
                search: {
                    firstName:   values.firstName   || undefined,
                    lastName:    values.lastName    || undefined,
                    city:        values.city        || undefined,
                    countryCode: values.countryCode || undefined,
                    state:       values.state       || undefined,
                    postalCode:  values.postalCode  || undefined,
                    profId:      values.profId      || undefined,
                    degree:      values.degree      || undefined,
                    specialty:   values.specialty   || undefined,
                    universe:    values.universe    || undefined,
                },
                user: {
                    team:      user?.team,
                    territory: user?.terr,
                },
                pageNumber: page,
                pageSize:   size,
            });
        },
        [searchExUsV3, user]
    );

    const handleSearch = useCallback(() => {
        const values = getValues();
        setPageNumber(1);
        runSearch(values, 1, pageSize);
    }, [getValues, pageSize, runSearch]);

    const handleApplyFilter = useCallback(() => {
        const values = getValues();
        setPageNumber(1);
        runSearch(values, 1, pageSize);
    }, [getValues, pageSize, runSearch]);

    const handleClearAll = useCallback(() => {
        reset(DEFAULT_VALUES);
        setPageNumber(1);
    }, [reset]);

    const handleClearFilter = useCallback(() => {
        setValue('degree',   '');
        setValue('specialty','');
        setValue('universe', '');
        // Re-run search with cleared filters
        const values = { ...getValues(), degree: '', specialty: '', universe: '' };
        setPageNumber(1);
        runSearch(values, 1, pageSize);
    }, [setValue, getValues, pageSize, runSearch]);

    const handlePageChange = useCallback((page: number) => {
        setPageNumber(page);
        runSearch(lastSearchRef.current, page, pageSize);
    }, [pageSize, runSearch]);

    const handlePageSizeChange = useCallback((size: number) => {
        setPageSize(size);
        setPageNumber(1);
        runSearch(lastSearchRef.current, 1, size);
    }, [runSearch]);

    const handlePreview = useCallback((row: ExUsSearchRowV3) => {
        setPreviewRow(row);
        setPreviewOpen(true);
        // Pre-fetch full details in the background so the drawer is data-rich
        if (row.profId) {
            fetchDetails({ PROF_ID: row.profId, user });
        }
    }, [fetchDetails, user]);

    const handleViewDetails = useCallback((row: ExUsSearchRowV3) => {
        // Navigate to v2 ExUsDetails page — v3 details rewrite is a future phase
        if (row.profId) {
            window.location.href = `/exus/details/${row.profId}`;
        }
    }, []);

    // -----------------------------------------------------------------------
    // Render
    // -----------------------------------------------------------------------

    const rows       = data?.rows       ?? [];
    const totalCount = data?.totalCount ?? 0;
    const hasResults = data !== undefined;

    return (
        <div className={styles.page}>
            {/* Search panel */}
            <SearchPanel
                register={register}
                setValue={setValue}
                onSearch={handleSearch}
                onClearAll={handleClearAll}
                isLoading={isLoading}
            />

            {/* Filter panel */}
            <FilterPanel
                register={register}
                onApply={handleApplyFilter}
                onClearFilter={handleClearFilter}
                isLoading={isLoading}
            />

            {/* Results area */}
            <div className={styles.resultsArea}>
                {isLoading && (
                    <div className={styles.landing} data-testid="loading-state">
                        Loading…
                    </div>
                )}

                {!isLoading && isError && (
                    <div className={styles.errorMsg} data-testid="error-state">
                        Error loading Ex-US Professionals. Please try again.
                    </div>
                )}

                {!isLoading && !isError && !hasResults && (
                    <div data-testid="landing-state">
                        <SearchLanding type="/exus" />
                    </div>
                )}

                {!isLoading && !isError && hasResults && (
                    <>
                        <PaginationBar
                            totalCount={totalCount}
                            pageNumber={pageNumber}
                            pageSize={pageSize}
                            onPageChange={handlePageChange}
                            onPageSizeChange={handlePageSizeChange}
                        />
                        <ExUsGrid
                            rows={rows}
                            userTeam={user?.team}
                            onPreview={handlePreview}
                            onViewDetails={handleViewDetails}
                        />
                        <PaginationBar
                            totalCount={totalCount}
                            pageNumber={pageNumber}
                            pageSize={pageSize}
                            onPageChange={handlePageChange}
                            onPageSizeChange={handlePageSizeChange}
                        />
                    </>
                )}
            </div>

            {/* Bottom action bar */}
            <div className={styles.bottomBar}>
                <button
                    type="button"
                    onClick={() => setShowAddWizard(true)}
                    data-testid="btn-addExUs"
                    style={{
                        background: '#3d3e8e',
                        color: '#fff',
                        border: 'none',
                        borderRadius: 3,
                        padding: '8px 18px',
                        fontSize: 13,
                        fontWeight: 600,
                        cursor: 'pointer',
                    }}
                >
                    ＋ ADD Ex-US Professional
                </button>
            </div>

            {/* Preview drawer (Ant Design) */}
            <Drawer
                title={
                    previewRow
                        ? `${previewRow.firstName ?? ''} ${previewRow.lastName ?? ''}, ${previewRow.degree ?? ''}`.trim()
                        : 'Preview'
                }
                placement="right"
                size={400}
                open={previewOpen}
                onClose={() => setPreviewOpen(false)}
                data-testid="preview-drawer"
            >
                {previewRow && <PreviewContent row={previewRow} />}
            </Drawer>

            {/* Add Ex-US wizard (preserved from v2) */}
            {showAddWizard && (
                // AddExUs is a legacy @ts-nocheck class component — cast through unknown to satisfy TS
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                <AddExUs
                    {...{ user, close: () => setShowAddWizard(false) } as any}
                />
            )}
        </div>
    );
}

